/**
 * Black Matte VTC - Partner Login Page
 * Pure view layer - NO business logic
 */

import React, { useState, useEffect } from 'react';
import Seo from '../../components/Seo';
import { seo } from '../../seo/seoData';
import { useNavigate, Link } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Building2, Loader2 } from 'lucide-react';

const PartnerLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading, error: authError } = usePartnerAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/partner/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/partner/dashboard');
    } catch (err) {
      // Display user-friendly error message
      const errorMsg = err.message || 'Authentication failed';
      if (errorMsg.includes('401') || errorMsg.toLowerCase().includes('invalid')) {
        setError('Invalid credentials. Please check your email and password.');
      } else if (errorMsg.includes('403') || errorMsg.toLowerCase().includes('suspended')) {
        setError('Your account is suspended. Please contact support.');
      } else if (errorMsg.includes('403') || errorMsg.toLowerCase().includes('pending')) {
        setError('Your account is pending approval.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Seo {...seo.partner} />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
              BLACK MATTE
            </h1>
            <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mt-1">
              Partner Portal
            </p>
          </Link>
        </div>

        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-neutral-800 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-neutral-400" />
              </div>
            </div>
            <CardTitle className="text-xl text-center text-white">
              Partner Sign In
            </CardTitle>
            <CardDescription className="text-center text-neutral-500">
              Access your business dashboard
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Display - Shows API errors directly */}
              {(error || authError) && (
                <div 
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm"
                  data-testid="login-error"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error || authError}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-neutral-300">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="partner@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-neutral-300">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="login-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-white text-black hover:bg-neutral-200"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-neutral-500">
                Not a partner yet?{' '}
                <Link to="/entreprises" className="text-white hover:underline">
                  Apply for partnership
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-neutral-600">
          By signing in, you agree to our{' '}
          <Link to="/legal/cgu" className="text-neutral-400 hover:text-white">
            Terms of Service
          </Link>
        </p>
      </div>
    </div>
  );
};

export default PartnerLogin;
